/**
 * Recipient groups admin page (PRD §5.6 D-04).
 *
 * Admins configure WHO gets an alert for a (site, event_type, channel)
 * triple. For Telegram: chat_ids. For Email: addresses. The classify
 * worker's dispatch path resolves the recipient list at confirm time.
 *
 * This is the UI surface for the `recipient_groups` table. The backing
 * REST endpoints live at /api/v1/recipients.
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Plus, Trash2, ToggleLeft, ToggleRight } from "lucide-react";
import { useState } from "react";
import { api, ApiError } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { AdminNav } from "@/components/AdminNav";

type Channel = "TELEGRAM" | "EMAIL";
type Role = "FIELD_TEAM" | "SUPERVISOR" | "READ_ONLY";

interface RecipientGroup {
  id: string;
  siteId: string;
  eventType: string;
  channel: Channel;
  recipients: string[];
  role: Role;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

const EVENT_TYPES = [
  "ALL",
  "INTRUSION_THIRD_PARTY",
  "LEAK_SIGNAL",
  "VEHICLE_VIBRATION",
  "MAINTENANCE_NOISE",
  "TRAIN_MOVEMENT",
  "UNKNOWN",
];

export function RecipientsAdminPage() {
  const { user } = useAuth();
  const qc = useQueryClient();
  const isAdmin = user?.role === "ADMIN";

  const { data, isLoading } = useQuery<{ groups: RecipientGroup[] }>({
    queryKey: ["admin", "recipients"],
    queryFn: () => api<{ groups: RecipientGroup[] }>("/recipients"),
  });

  const [form, setForm] = useState({
    site_id: "SITE_NORTH_PIPELINE",
    event_type: "ALL",
    channel: "TELEGRAM" as Channel,
    recipients_raw: "",
    role: "FIELD_TEAM" as Role,
  });
  const [error, setError] = useState<string | null>(null);

  const createMut = useMutation({
    mutationFn: () => {
      const list = form.recipients_raw
        .split(/[,\n]/)
        .map((s) => s.trim())
        .filter(Boolean);
      if (list.length === 0) {
        throw new ApiError(400, "at least one recipient required");
      }
      return api<{ group: RecipientGroup }>("/recipients", {
        method: "POST",
        body: JSON.stringify({
          site_id: form.site_id,
          event_type: form.event_type,
          channel: form.channel,
          recipients: list,
          role: form.role,
          is_active: true,
        }),
      });
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin", "recipients"] });
      setForm({ ...form, recipients_raw: "" });
      setError(null);
    },
    onError: (err: unknown) =>
      setError(err instanceof ApiError ? err.message : "create failed"),
  });

  const toggleMut = useMutation({
    mutationFn: (group: RecipientGroup) =>
      api<{ group: RecipientGroup }>(`/recipients/${group.id}`, {
        method: "PATCH",
        body: JSON.stringify({ is_active: !group.isActive }),
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["admin", "recipients"] }),
    onError: (err: unknown) =>
      setError(err instanceof ApiError ? err.message : "toggle failed"),
  });

  const deleteMut = useMutation({
    mutationFn: (id: string) =>
      api<{ deleted: string }>(`/recipients/${id}`, { method: "DELETE" }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["admin", "recipients"] }),
    onError: (err: unknown) =>
      setError(err instanceof ApiError ? err.message : "delete failed"),
  });

  return (
    <div className="space-y-5" data-testid="recipients-admin-page">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <AdminNav />
        <span className="text-xs text-slate-500">
          {isAdmin ? "ADMIN — you can manage" : "Read-only (requires ADMIN)"}
        </span>
      </div>

      {error && (
        <div
          className="text-xs text-rose-400 border border-rose-900/50 bg-rose-950/30 rounded px-3 py-2"
          data-testid="admin-recipients-error"
        >
          {error}
        </div>
      )}

      <section className="rounded-lg border border-slate-800 bg-slate-900/40 p-4">
        <h2 className="text-sm font-semibold text-slate-200 mb-3">
          Current alert recipient groups
        </h2>

        {isLoading ? (
          <div className="text-xs text-slate-500 italic" data-testid="admin-recipients-loading">
            Loading…
          </div>
        ) : (data?.groups ?? []).length === 0 ? (
          <div className="text-xs text-slate-500 italic" data-testid="admin-recipients-empty">
            No recipient groups configured yet. Add one below to start receiving alerts.
          </div>
        ) : (
          <table className="w-full text-xs" data-testid="admin-recipients-table">
            <thead>
              <tr className="text-left text-[11px] uppercase tracking-wider text-slate-500 border-b border-slate-800">
                <th className="py-1 font-medium">site</th>
                <th className="py-1 font-medium">event_type</th>
                <th className="py-1 font-medium">channel</th>
                <th className="py-1 font-medium">recipients</th>
                <th className="py-1 font-medium">role</th>
                <th className="py-1 font-medium">active</th>
                <th className="py-1 font-medium"></th>
              </tr>
            </thead>
            <tbody>
              {(data?.groups ?? []).map((g) => (
                <tr
                  key={g.id}
                  className="border-b border-slate-900"
                  data-testid={`admin-recipient-row-${g.id}`}
                >
                  <td className="py-1.5 font-mono text-slate-300">{g.siteId}</td>
                  <td className="py-1.5 font-mono text-slate-300">{g.eventType}</td>
                  <td className="py-1.5 font-mono text-slate-300">{g.channel}</td>
                  <td className="py-1.5 font-mono text-slate-400">
                    {g.recipients.length > 0
                      ? g.recipients.join(", ")
                      : <span className="italic text-slate-600">empty</span>}
                  </td>
                  <td className="py-1.5 text-slate-400">{g.role}</td>
                  <td className="py-1.5">
                    <button
                      type="button"
                      disabled={!isAdmin}
                      onClick={() => toggleMut.mutate(g)}
                      data-testid={`admin-recipient-${g.id}-toggle`}
                      className="disabled:opacity-40"
                      title={isAdmin ? "toggle active" : "ADMIN only"}
                    >
                      {g.isActive ? (
                        <ToggleRight className="size-4 text-teal-400" />
                      ) : (
                        <ToggleLeft className="size-4 text-slate-600" />
                      )}
                    </button>
                  </td>
                  <td className="py-1.5 text-right">
                    {isAdmin && (
                      <button
                        type="button"
                        onClick={() => {
                          if (confirm(`Delete recipient group ${g.siteId}/${g.eventType}/${g.channel}?`)) {
                            deleteMut.mutate(g.id);
                          }
                        }}
                        data-testid={`admin-recipient-${g.id}-delete`}
                        className="text-rose-400 hover:text-rose-300"
                      >
                        <Trash2 className="size-3" />
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>

      {isAdmin && (
        <section className="rounded-lg border border-slate-800 bg-slate-900/40 p-4">
          <h2 className="text-sm font-semibold text-slate-200 mb-3">
            Add recipient group
          </h2>
          <form
            className="space-y-2"
            data-testid="admin-recipients-create"
            onSubmit={(e) => {
              e.preventDefault();
              createMut.mutate();
            }}
          >
            <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
              <label className="block">
                <span className="text-[11px] text-slate-500 uppercase tracking-wider">
                  Site ID
                </span>
                <input
                  type="text"
                  value={form.site_id}
                  onChange={(e) => setForm({ ...form, site_id: e.target.value })}
                  data-testid="admin-recipients-site"
                  className="mt-0.5 w-full bg-slate-950 border border-slate-700 rounded px-2 py-1 text-xs font-mono"
                />
              </label>
              <label className="block">
                <span className="text-[11px] text-slate-500 uppercase tracking-wider">
                  Event type
                </span>
                <select
                  value={form.event_type}
                  onChange={(e) =>
                    setForm({ ...form, event_type: e.target.value })
                  }
                  data-testid="admin-recipients-event-type"
                  className="mt-0.5 w-full bg-slate-950 border border-slate-700 rounded px-2 py-1 text-xs font-mono"
                >
                  {EVENT_TYPES.map((t) => (
                    <option key={t} value={t}>
                      {t}
                    </option>
                  ))}
                </select>
              </label>
              <label className="block">
                <span className="text-[11px] text-slate-500 uppercase tracking-wider">
                  Channel
                </span>
                <select
                  value={form.channel}
                  onChange={(e) =>
                    setForm({ ...form, channel: e.target.value as Channel })
                  }
                  data-testid="admin-recipients-channel"
                  className="mt-0.5 w-full bg-slate-950 border border-slate-700 rounded px-2 py-1 text-xs font-mono"
                >
                  <option value="TELEGRAM">TELEGRAM</option>
                  <option value="EMAIL">EMAIL</option>
                </select>
              </label>
            </div>
            <label className="block">
              <span className="text-[11px] text-slate-500 uppercase tracking-wider">
                Recipients
                <span className="ml-2 normal-case text-slate-600">
                  {form.channel === "TELEGRAM"
                    ? "(chat IDs, one per line or comma-separated)"
                    : "(email addresses, one per line or comma-separated)"}
                </span>
              </span>
              <textarea
                rows={3}
                value={form.recipients_raw}
                onChange={(e) =>
                  setForm({ ...form, recipients_raw: e.target.value })
                }
                data-testid="admin-recipients-list"
                placeholder={
                  form.channel === "TELEGRAM"
                    ? "8293205635\n8234567890"
                    : "field-team@example.com\nsupervisor@example.com"
                }
                className="mt-0.5 w-full bg-slate-950 border border-slate-700 rounded px-2 py-1 text-xs font-mono"
              />
            </label>
            <label className="block">
              <span className="text-[11px] text-slate-500 uppercase tracking-wider">
                Role
              </span>
              <select
                value={form.role}
                onChange={(e) => setForm({ ...form, role: e.target.value as Role })}
                data-testid="admin-recipients-role"
                className="mt-0.5 w-full md:w-1/3 bg-slate-950 border border-slate-700 rounded px-2 py-1 text-xs font-mono"
              >
                <option value="FIELD_TEAM">FIELD_TEAM</option>
                <option value="SUPERVISOR">SUPERVISOR</option>
                <option value="READ_ONLY">READ_ONLY</option>
              </select>
            </label>
            <button
              type="submit"
              disabled={createMut.isPending}
              data-testid="admin-recipients-submit"
              className="text-xs inline-flex items-center gap-1 bg-teal-400 hover:bg-teal-300 text-slate-900 font-semibold px-3 py-1.5 rounded disabled:opacity-50"
            >
              <Plus className="size-3" />
              {createMut.isPending ? "Adding…" : "Add group"}
            </button>
          </form>
        </section>
      )}
    </div>
  );
}
